import React, { useState } from 'react';
import clsx from 'clsx';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import { PanelTopSearch } from '@/components/panels/PanelTopSearch/PanelTopSearch';
import { ScrollArea } from '@/components/ui/scroll-area';

import { ElementCard } from '@/components/panels/elements/ElementCard/ElementCard';

import ElementBlank from '@/assets/images/elements-png/element-blank.png';
import ElementButton from '@/assets/images/elements-png/element-button.png';
import ElementCountdown from '@/assets/images/elements-png/element-countdown.png';
import ElementForm from '@/assets/images/elements-png/element-form.png';
import ElementIcon from '@/assets/images/elements-png/element-icon.png';
import ElementImage from '@/assets/images/elements-png/element-image.png';
import ElementText from '@/assets/images/elements-png/element-text.png';
import ElementVideo from '@/assets/images/elements-png/element-video.png';
import ElementHtml from '@/assets/images/elements-png/element-html.png';

import ElementBlankHover from '@/assets/images/elements-png/element-blank-hover.png';
import ElementButtonHover from '@/assets/images/elements-png/element-button-hover.png';
import ElementCountdownHover from '@/assets/images/elements-png/element-countdown-hover.png';
import ElementFormHover from '@/assets/images/elements-png/element-form-hover.png';
import ElementIconHover from '@/assets/images/elements-png/element-icon-hover.png';
import ElementImageHover from '@/assets/images/elements-png/element-image-hover.png';
import ElementTextHover from '@/assets/images/elements-png/element-text-hover.png';
import ElementVideoHover from '@/assets/images/elements-png/element-video-hover.png';
import ElementHtmlHover from '@/assets/images/elements-png/element-html-hover.png';
import ElementPopup from '@/assets/images/elements-png/element-popup.png';
import ElementPopupHover from '@/assets/images/elements-png/element-popup-hover.png';

import { useEditor, Element as CraftElement, type NodeTree, ROOT_NODE } from '@craftjs/core';
import Button from '@/components/userComponents/Button';
import Countdown from '@/components/userComponents/Countdown';
import Text from '@/components/userComponents/Text';
import EditorContainer from '@/components/userComponents/Container';
import EditorPopup from '@/components/userComponents/Popup';

import Image from '@/components/userComponents/Image';
import Video from '@/components/userComponents/Video';
import Icon from '@/components/userComponents/Icon';
import Embed from '@/components/userComponents/Embed';
import Form from '@/components/userComponents/Form';
import EditorFormTextField from '@/components/userComponents/Form/fields/FormTextField';
import EditorFormEmailField from '@/components/userComponents/Form/fields/FormEmailField';
import EditorFormPhoneField from '@/components/userComponents/Form/fields/FormPhoneField';
import { isContainerNode, isPageNode, getNextPopupNumber } from '@/utils/helperFunc';
import {
  FORM_NODE_NAME,
  FORM_TEXT_FIELD_NODE_NAME,
  FORM_EMAIL_FIELD_NODE_NAME,
  FORM_PHONE_FIELD_NODE_NAME,
} from '@/utils/constants';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { useScrollNodeIntoView } from '@/hooks/useScrollNodeIntoView';
import EditorFormSubmitButton from '@/components/userComponents/Form/buttons/FormSubmitButton';
import { isFormSubNode } from '@builder4/shared/components/Form/formRegistry';
import { setActivePanel } from '@/store/assetsPanel/assetsPanelSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectFeatureFlags } from '@/store/featureFlags/selectors';

interface ElementProps {
  className: string;
  onElementClick?: (elementName: string) => void;
  onHidePanel: (value: boolean) => void;
}

const elements = [
  {
    text: 'Container',
    icon: ElementBlank,
    iconSrcHover: ElementBlankHover,
    elementName: 'Blank',
    bg: 'hover:bg-indigo-100',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(98,69,242,0.30)]',
  },
  {
    text: 'Image',
    icon: ElementImage,
    iconSrcHover: ElementImageHover,
    elementName: 'Image',
    bg: 'hover:bg-emerald-500/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(16,185,109,0.40)]',
  },
  {
    text: 'Text',
    icon: ElementText,
    iconSrcHover: ElementTextHover,
    elementName: 'Text',
    bg: 'hover:bg-teal-500/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(25,176,175,0.40)]',
  },
  {
    text: 'Video',
    icon: ElementVideo,
    iconSrcHover: ElementVideoHover,
    elementName: 'Video',
    bg: 'hover:bg-red-400/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(220,53,38,0.40)]',
  },
  {
    text: 'Embed',
    icon: ElementHtml,
    iconSrcHover: ElementHtmlHover,
    elementName: 'Embed',
    featureFlag: 'page-studio-embed',
    bg: 'hover:bg-purple-500/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(147,51,234,0.40)]',
  },
  {
    text: 'Button',
    icon: ElementButton,
    iconSrcHover: ElementButtonHover,
    elementName: 'Button',
    bg: 'hover:bg-orange-500/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(251,129,36,0.40)]',
  },
  {
    text: 'Icon',
    icon: ElementIcon,
    iconSrcHover: ElementIconHover,
    elementName: 'Icon',
    featureFlag: 'page-studio-icon',
    bg: 'hover:bg-purple-500/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(139,92,246,0.40)]',
  },
  {
    text: 'Form',
    icon: ElementForm,
    iconSrcHover: ElementFormHover,
    elementName: 'Form',
    bg: 'hover:bg-lime-500/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(168,185,16,0.40)]',
  },
  {
    text: 'Countdown',
    icon: ElementCountdown,
    iconSrcHover: ElementCountdownHover,
    elementName: 'Countdown',
    bg: 'hover:bg-sky-600/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(16,115,185,0.40)]',
  },
  {
    text: 'Popup',
    icon: ElementPopup,
    iconSrcHover: ElementPopupHover,
    elementName: 'Popup',
    featureFlag: 'page-studio-popup',
    bg: 'hover:bg-purple-500/20',
    classNameImg: 'group-hover:shadow-[0px_2px_6px_0px_rgba(147,51,234,0.40)]',
  },
];

/**
 * Defines the default structure/fields for a form on creation.

 * TODO: Where should this abstracted default live?
 * @returns The default structure/fields for a form on creation.
 */
const defaultFormContent: React.ReactNode = (
  <Form>
    <EditorFormTextField label="Name" placeholder="Enter your name" />
    <EditorFormEmailField
      label="Email"
      placeholder="Enter your email"
      required={true}
      primaryEmailField={true}
    />
    <EditorFormSubmitButton />
  </Form>
);

export const Element: React.FC<ElementProps> = ({ className, onElementClick, onHidePanel }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const dispatch = useAppDispatch();
  const { connectors, actions, query, selected } = useEditor((_, query) => ({
    selected: query.getEvent('selected').first(),
  }));
  const featureFlags = useAppSelector(selectFeatureFlags);

  /**
   * Creates the Craft NodeTree representation for a panel element.
   * This function maps a panel element name to its corresponding React
   * user component and converts it into a NodeTree.
   *
   * Used by both click-to-add and drag-to-add flows to define the default
   * props, structure, and initial children for newly created builder elements.
   *
   * @param elementName - The identifier of the selected panel element.
   * @returns The NodeTree for the element, or null if unsupported.
   */
  const getUserComponent = (elementName: string): NodeTree | null => {
    let nodeTree: NodeTree | null = null;
    switch (elementName) {
      case 'Blank':
        nodeTree = query
          .parseReactElement(<CraftElement canvas is={EditorContainer} />)
          .toNodeTree();
        break;
      case 'Button':
        nodeTree = query.parseReactElement(<Button />).toNodeTree();
        break;
      case 'Countdown':
        nodeTree = query
          .parseReactElement(<Countdown backgroundColor={{ r: 0, g: 0, b: 0, a: 1 }} />)
          .toNodeTree();
        break;
      case 'Text':
        nodeTree = query.parseReactElement(<Text />).toNodeTree();
        break;
      case 'Image':
        nodeTree = query.parseReactElement(<Image />).toNodeTree();
        break;
      case 'Video':
        nodeTree = query.parseReactElement(<Video />).toNodeTree();
        break;
      case 'Icon':
        nodeTree = query.parseReactElement(<Icon />).toNodeTree();
        break;
      case 'Embed':
        nodeTree = query.parseReactElement(<Embed />).toNodeTree();
        break;
      case 'Form':
        nodeTree = query.parseReactElement(defaultFormContent).toNodeTree();
        break;
      case FORM_TEXT_FIELD_NODE_NAME:
        nodeTree = query.parseReactElement(<EditorFormTextField />).toNodeTree();
        break;
      case FORM_EMAIL_FIELD_NODE_NAME:
        nodeTree = query.parseReactElement(<EditorFormEmailField />).toNodeTree();
        break;
      case FORM_PHONE_FIELD_NODE_NAME:
        nodeTree = query.parseReactElement(<EditorFormPhoneField />).toNodeTree();
        break;
      case 'Popup':
        nodeTree = query
          .parseReactElement(
            <CraftElement canvas is={EditorPopup}>
              <CraftElement canvas is={EditorContainer} height="100%" />
            </CraftElement>,
          )
          .toNodeTree();
        break;
      default:
        //this is working code for loading sections in craft
        // const template = localStorage.getItem('demoTemplate') ||"";
        // if(!template) return null
        // const templateNodeTree = JSON.parse(template);
        // const newNodes = JSON.parse(templateNodeTree.nodes);
        // const nodePairs: [string, Node|SerializedNode][] = Object.keys(newNodes).map((id) => {
        //     let nodeId = id
        //     return [nodeId, query.parseSerializedNode(newNodes[id]).toNode((node) => (node.id = nodeId))]
        // })
        // const tree = { rootNodeId: templateNodeTree.rootNodeId, nodes: fromEntries(nodePairs) }
        // nodeTree = getCloneTree(query, tree)
        break;
    }
    return nodeTree;
  };

  const handleClosePanel = (elementName: string) => {
    if (onElementClick) {
      onElementClick(elementName);
    }
  };

  const filteredElements = elements.filter((el) => {
    if (el.featureFlag && !featureFlags.includes(el.featureFlag)) {
      return false;
    }
    return el.text.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const { setNodeDimensionsOnCreation } = useCraftHelper();
  const scrollNodeIntoView = useScrollNodeIntoView();

  const addElementToSelectedLayer = (elementName: string) => {
    if (!selected && elementName !== 'Popup') {
      console.warn('No active layer selected.');
      return;
    }

    // Popups must be added directly to the root (Page)
    if (elementName === 'Popup') {
      const nodeTree = getUserComponent(elementName);
      if (nodeTree) {
        const nextNumber = getNextPopupNumber(query);
        actions.addNodeTree(nodeTree, ROOT_NODE);
        actions.setCustom(nodeTree.rootNodeId, (custom) => {
          custom.displayName = `Popup ${nextNumber}`;
        });
        setNodeDimensionsOnCreation(nodeTree);
        scrollNodeIntoView(nodeTree.rootNodeId, true);
      }
      return;
    }

    let targetNodeId = selected;
    const selectedNode = query.node(targetNodeId).get();
    if (!selectedNode) {
      console.warn('Selected node not found.');
      return;
    }

    const resolveFormTargetNode = (nodeId: string | null): string | null => {
      let currentId: string | null = nodeId;
      while (currentId) {
        const currentNode = query.node(currentId).get();
        if (!currentNode) return null;
        if (currentNode.data.displayName === FORM_NODE_NAME) {
          return currentId;
        }
        currentId = (currentNode.data.parent as string | null) ?? null;
      }
      return null;
    };

    if (isFormSubNode(elementName)) {
      const formNodeId = resolveFormTargetNode(selected);
      if (!formNodeId) {
        console.warn('Form sub-elements can only be added inside a Form element.');
        return;
      }
      targetNodeId = formNodeId;
    } else if (!isContainerNode(selectedNode)) {
      // For non-container selections, fall back to parent resolution
      const isPage = isPageNode(selectedNode);
      if (isPage && elementName === 'Blank') {
        targetNodeId = selected;
      } else {
        const parentId = selectedNode.data.parent;
        if (!parentId) {
          console.warn('No parent found for selected node.');
          return;
        }
        const parentNode = query.node(parentId).get();
        if (!parentNode) {
          console.warn('Parent node not found.');
          return;
        }
        targetNodeId = parentId;
      }
    }
    const nodeTree = getUserComponent(elementName);
    if (nodeTree) {
      actions.addNodeTree(nodeTree, targetNodeId);
      setNodeDimensionsOnCreation(nodeTree);
    }
  };

  const handleElementSelect = (elementName: string) => {
    addElementToSelectedLayer(elementName);
  };
  return (
    <div className={clsx(className, 'flex h-full max-w-[280px] flex-col')}>
      <PanelTopHeader
        text="Add Element"
        backButton={false}
        onClickClose={() => handleClosePanel('')}
        className={''}
      />

      <PanelTopSearch
        placeholder="Search element..."
        text="Search"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
        onSearch={(value: string) => setSearchTerm(value)}
        className={''}
      />

      <ScrollArea className="flex w-full flex-1 gap-1 overflow-hidden px-6">
        <div className="flex flex-col gap-4 pb-4">
          {filteredElements.length === 0 ? (
            <p className="text-button-sm text-center text-neutral-500">No elements found</p>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-subtitle-xs font-medium text-neutral-700">Basic</p>
              <div className="grid grid-cols-2 gap-2 px-[2px]">
                {filteredElements.map(
                  ({ text, icon, elementName, bg, classNameImg, iconSrcHover }) => (
                    <ElementCard
                      key={text}
                      className={bg}
                      text={text}
                      imgSrc={icon}
                      classNameImg={classNameImg}
                      imageHover={iconSrcHover || ''}
                      state="default"
                      type={'default'}
                      data-qa-selector={`add-element-${elementName.toLowerCase()}-button`}
                      onClick={() => handleElementSelect(elementName)}
                      onDragStart={() => {
                        onHidePanel(true);
                      }}
                      onDragEnd={() => {
                        onHidePanel(false);
                        dispatch(setActivePanel(''));
                      }}
                      ref={(ref) => {
                        if (ref) {
                          const factory = () => {
                            const nt = getUserComponent(elementName);
                            if (!nt) {
                              console.error(
                                `Failed to create NodeTree for element: ${elementName}`,
                              );
                            }
                            return nt as NodeTree;
                          };

                          connectors.create(ref, factory, {
                            onCreate: (nodeTree) => {
                              if (elementName === 'Popup') {
                                const nextNumber = getNextPopupNumber(query);
                                actions.setCustom(nodeTree.rootNodeId, (custom) => {
                                  custom.displayName = `Popup ${nextNumber}`;
                                });
                              }
                              setNodeDimensionsOnCreation(nodeTree);
                              if (elementName === 'Popup') {
                                scrollNodeIntoView(nodeTree.rootNodeId, true);
                              }
                            },
                          });
                        }
                      }}
                    />
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
